import React, { createRef, useContext, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { Tooltip } from '@mui/material'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import EditIcon from '@mui/icons-material/Edit'

import { getRoute, routePaths, userRightTypes } from '../../../../constants'
import { Logger, LoggerTypes, isDebug } from '../../../../utils'
import { searchProjects } from '../../../../actions'
import { LoaderContext } from '../../../../contexts'
import { PageTitle, DataGrid, DataGridRowLink, ButtonIcon } from '../../../../components'
import { ProjectButtonFavorite } from '../../ProjectButtonFavorite'
import { ProjectDialogUpdate, ProjectDialogDelete } from '../../dialogs'
import { checkPermissions } from '../../../../components/AccessControl'

export const ProjectArchivedList = ({ archived, onUpdateProject }) => {
  const [searchValue, setSearchValue] = useState('')

  const dispatch = useDispatch()
  const ref = createRef()
  const { t } = useTranslation()

  const isLoading = useContext(LoaderContext)

  function onSearch(txt) {
    setSearchValue(txt)
    dispatch(searchProjects(txt))
  }

  const onCellEdit = (el) => {
    const projectUpdated = {
      ...el.row,
      active: el.value
    }

    Logger.trace(LoggerTypes.CONTROLLER, 'ProjectActiveList.onCellEdit()', { el, projectUpdated })

    if (el.field === 'active') {
      onUpdateProject(projectUpdated)
    }
  }

  const COLUMN_HEADER = [
    { field: 'id' },
    {
      field: 'name',
      type: 'string',
      headerName: t('projects.table.columns.name'),
      flex: 1,
      minWidth: 200,
      cellClassName: 'projectNameCell',
      renderCell: (e) => (
        <Tooltip ref={ref} title={e?.row?.description || ''}>
          <DataGridRowLink href={getRoute(routePaths.SCENARIO_LIST, { pid: e.row.id })} text={e.formattedValue} />
        </Tooltip>
      )
    },
    /*
    {
      field: 'nb_simulations',
      type: 'integer',
      headerName: t('projects.table.columns.nb_simulations'),
      width: 140,
      editable: true,
    },
    */
    {
      field: 'number',
      type: 'string',
      headerName: t('projects.table.columns.number'),
      width: 100,
      editable: false
    },
    {
      field: 'scenario_count',
      type: 'number',
      headerName: t('projects.table.columns.scenario_count'),
      width: 100,
      editable: false
    },
    {
      field: 'scenario_officials',
      type: 'number',
      headerName: t('projects.table.columns.scenario_officials'),
      width: 100,
      editable: false
    },
    {
      field: 'update_user',
      type: 'string',
      headerName: t('projects.table.columns.author'),
      width: 200,
      editable: false
    },
    {
      field: 'creation_date',
      type: 'string',
      headerName: t('projects.table.columns.created_on'),
      width: 110,
      editable: false
    },
    {
      field: 'last_update_date',
      type: 'string',
      headerName: t('projects.table.columns.last_update'),
      width: 110,
      editable: false
    },
    {
      field: 'active',
      type: 'boolean',
      headerName: t('projects.table.columns.active'),
      width: 90,
      editable: true
    },
    {
      field: 'favorite',
      type: 'boolean',
      headerName: t('projects.table.columns.favorite'),
      width: 90,
      editable: false,
      renderCell: (e) => <ProjectButtonFavorite ref={ref} project={e.row} />
    }
  ]

  // Logger.trace(LoggerTypes.CONTAINER, 'ProjectArchivedList.render()', { archived })

  return (
    <div className="ProjectArchivedList">
      <PageTitle title={t('projects.table.types.archived')} />
      <DataGrid
        actionsButtons={[
          <ButtonIcon
            disabled={!checkPermissions([userRightTypes.PROJECT_EDIT])}
            key={'button-edit'}
            DialogClass={ProjectDialogUpdate}
            icon={<EditIcon />}
            tooltip={t('common.update')}
          />,
          <ButtonIcon
            disabled={!checkPermissions([userRightTypes.PROJECT_DELETE])}
            key={'button-delete'}
            DialogClass={ProjectDialogDelete}
            icon={<DeleteIcon />}
            tooltip={t('common.delete')}
          />
        ]}
        columns={COLUMN_HEADER}
        columnVisibilityModel={{ id: isDebug() }}
        rows={archived}
        isLoading={isLoading}
        initialState={{
          columns: {
            columnVisibilityModel: {
              id: false
            }
          }
        }}
        searchValue={searchValue}
        onSearch={onSearch}
        onCellEditCommit={onCellEdit}
      />
    </div>
  )
}

ProjectArchivedList.displayName = 'ProjectArchivedList'

ProjectArchivedList.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  archived: PropTypes.array.isRequired,
  onUpdateProject: PropTypes.func.isRequired
}

ProjectArchivedList.defaultProps = {
  isLoading: true,
  archived: [],
  onUpdateProject: () => {}
}
